import { supabase } from '@/lib/supabaseClient';
import type { KnowledgeSourceType } from './knowledgeSourceManager';

/**
 * AIC-1.1 — Lexical RAG retrieval (no embedding vendor).
 *
 * Single retrieval interface (AC4) over the `search_knowledge` Postgres function
 * (FTS + pg_trgm fallback). The implementation — Postgres FTS today, Voyage
 * embeddings later — is hidden behind `retrieveKnowledge()`; callers never touch
 * the search mechanism. There is intentionally NO call to OpenAI or the
 * `generate-embedding` edge function anywhere in this path (AC6).
 */

/** A row as returned by the `search_knowledge` SQL function. */
export interface KnowledgeRow {
  id: string;
  title: string;
  body: string;
  source_type: KnowledgeSourceType;
  score: number;
  match_type: 'fts' | 'trgm';
}

/** A normalized retrieval result handed back to callers. */
export interface KnowledgeResult {
  id: string;
  title: string;
  body: string;
  source_type: KnowledgeSourceType;
  score: number;
  match_type: 'fts' | 'trgm';
}

export interface RetrieveResult {
  results: KnowledgeResult[];
  /** 0..1 confidence used by routing (AIC-2.2) and the low-confidence guardrail (AIC-3.2). */
  confidence: number;
}

/**
 * Pluggable query-expansion seam (AC3). Turns a natural question into extra
 * search terms/synonyms to recover paraphrase recall. The real Haiku-backed
 * expander lands with the backend Claude client in AIC-2.1; until then this is
 * left unset and retrieval runs on the raw query. Expansion is always
 * best-effort — a failure or absence must never error the turn.
 */
export type QueryExpander = (query: string) => Promise<string[]>;

export interface RetrieveOptions {
  matchCount?: number;
  sourceTypes?: KnowledgeSourceType[];
  expandQuery?: QueryExpander;
}

/**
 * Combine the raw query with expansion terms into a single websearch query
 * string. The raw phrase stays intact (precise, high-ranking) and each new
 * synonym is OR'd on to broaden recall. Terms already present in the raw query
 * (case-insensitive) and blanks are dropped.
 */
export function buildSearchQuery(rawQuery: string, expansionTerms: string[]): string {
  const raw = (rawQuery ?? '').trim();
  const rawLower = raw.toLowerCase();
  const seen = new Set<string>();
  const extra: string[] = [];

  for (const term of expansionTerms ?? []) {
    const t = (term ?? '').trim();
    if (!t) continue;
    const lower = t.toLowerCase();
    if (rawLower.includes(lower) || seen.has(lower)) continue;
    seen.add(lower);
    extra.push(t);
  }

  if (!raw) return extra.join(' or ');
  if (extra.length === 0) return raw;
  return [raw, ...extra].join(' or ');
}

/** Overall confidence = top score (SQL scores are already 0..1), clamped. */
export function normalizeConfidence(results: Pick<KnowledgeResult, 'score'>[]): number {
  if (!results || results.length === 0) return 0;
  const top = Math.max(...results.map((r) => r.score ?? 0));
  return Math.min(1, Math.max(0, top));
}

/** Map raw RPC rows to results; tolerate null/undefined payloads. */
export function mapRows(rows: KnowledgeRow[]): KnowledgeResult[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    body: r.body,
    source_type: r.source_type,
    score: r.score,
    match_type: r.match_type,
  }));
}

/** Coerce a source-type filter: undefined/null/empty-array all mean "no filter" (all types). */
export function normalizeSourceTypes(
  sourceTypes: KnowledgeSourceType[] | undefined | null,
): KnowledgeSourceType[] | null {
  return sourceTypes && sourceTypes.length > 0 ? sourceTypes : null;
}

/** Run the expander best-effort: never throw, fall back to no expansion. */
export async function safeExpand(
  expander: QueryExpander | undefined,
  query: string,
): Promise<string[]> {
  if (!expander) return [];
  try {
    const terms = await expander(query);
    return Array.isArray(terms) ? terms : [];
  } catch (err) {
    console.warn('[retrieveKnowledge] query expansion failed, using raw query:', err);
    return [];
  }
}

/**
 * Retrieve curated knowledge for a query, scoped to the caller's tenant + global
 * content. Authoritative tenant scoping happens server-side in `search_knowledge`
 * (via get_current_user_tenant_id); `tenantId` is passed as the documented
 * fallback for contexts without an auth session.
 */
export async function retrieveKnowledge(
  query: string,
  tenantId: string,
  opts: RetrieveOptions = {},
): Promise<RetrieveResult> {
  const expansionTerms = await safeExpand(opts.expandQuery, query);
  const searchQuery = buildSearchQuery(query, expansionTerms);

  const { data, error } = await supabase.rpc('search_knowledge', {
    p_query: searchQuery,
    p_tenant_id: tenantId,
    p_match_count: opts.matchCount ?? 8,
    p_source_types: normalizeSourceTypes(opts.sourceTypes),
  });

  if (error) {
    console.error('[retrieveKnowledge] search_knowledge failed:', error);
    return { results: [], confidence: 0 };
  }

  const results = mapRows((data ?? []) as KnowledgeRow[]);
  return { results, confidence: normalizeConfidence(results) };
}
